import { useCallback, useRef } from 'react'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { SxProps } from '@mui/material/styles'
import TextField from '@mui/material/TextField'

type Props<Values extends FieldValues> = {
  name: FieldPath<Values>
  control: Control<Values>
  label?: string
  helperText?: string
  sx?: SxProps
  disabled?: boolean
  defaultCountry?: CountryIso2
}

export const PhoneFieldController = <Values extends FieldValues>({
  name,
  control,
  label,
  helperText,
  sx,
  disabled,
  defaultCountry = 'cz',
}: Props<Values>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref }, fieldState }) => (
        <PhoneField
          value={value || ''}
          onChange={onChange}
          fieldRef={ref}
          defaultCountry={defaultCountry}
          label={label}
          helperText={fieldState.error?.message ?? helperText ?? undefined}
          error={!!fieldState.error}
          disabled={disabled}
          sx={sx}
        />
      )}
    />
  )
}

type PhoneFieldProps = {
  value: string
  onChange: (value: string) => void
  fieldRef: React.Ref<HTMLInputElement>
  defaultCountry: CountryIso2
  label?: string
  helperText?: string
  error?: boolean
  disabled?: boolean
  sx?: SxProps
}

const PhoneField = ({
  value,
  onChange,
  fieldRef,
  defaultCountry,
  label,
  helperText,
  error,
  disabled,
  sx,
}: PhoneFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { inputValue, handlePhoneValueChange, country, setCountry } = usePhoneInput({
    defaultCountry,
    value,
    onChange: (data) => {
      onChange(data.phone)
    },
    inputRef: inputRef as React.MutableRefObject<HTMLInputElement | null>,
  })

  // Merge refs: hook's inputRef + Controller's ref
  const mergedRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el
      if (typeof fieldRef === 'function') {
        fieldRef(el)
      }
    },
    [fieldRef],
  )

  return (
    <TextField
      inputRef={mergedRef}
      value={inputValue}
      onChange={handlePhoneValueChange}
      variant="filled"
      hiddenLabel
      label={label}
      fullWidth
      size="small"
      disabled={disabled}
      error={error}
      helperText={helperText}
      sx={sx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0 }}>
            <Select
              size="small"
              variant="standard"
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              disableUnderline
              renderValue={(iso2) => <FlagImage iso2={iso2} size="24px" />}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: 0,
                  pr: '8px !important',
                },
              }}
            >
              {defaultCountries.map((c) => {
                const parsed = parseCountry(c)
                return (
                  <MenuItem key={parsed.iso2} value={parsed.iso2}>
                    <FlagImage iso2={parsed.iso2} size="20px" style={{ marginRight: 8 }} />
                    {parsed.name} +{parsed.dialCode}
                  </MenuItem>
                )
              })}
            </Select>
          </InputAdornment>
        ),
      }}
    />
  )
}
